/**
 * Tool→Module Mapping
 *
 * Maps internal Epicor tool names to user-facing module labels.
 * Users never see "epicor_get_vendor" — they see "Querying AP data..."
 *
 * Used by:
 * - ProgressIndicator (streaming phase labels)
 * - StreamingMessage (tool badge labels)
 */

const MODULE_MAP = [
  // Web search (server-side tool) — match first so it never falls through to
  // the default label.
  { pattern: /web_search|web search/, module: 'Web Search', verb: 'Searching the web' },
  // Knowledge base + universal search (meta tools) — map before the data
  // tools so they get real labels instead of the generic default.
  { pattern: /knowledge/, module: 'Knowledge', verb: 'Searching knowledge base' },
  { pattern: /universal_search|universal/, module: 'Search', verb: 'Searching all records' },
  // Customer tools
  { pattern: /customer|cust_/, module: 'Customers', verb: 'Querying customer data' },
  { pattern: /credit/, module: 'Customers', verb: 'Checking credit status' },

  // Order tools
  { pattern: /order|ship_date|backlog/, module: 'Orders', verb: 'Querying order data' },

  // Quote tools
  { pattern: /quote|benchmark/, module: 'Quotes', verb: 'Querying quote data' },

  // Job tools
  { pattern: /job|late_job|overrun|production|capacity/, module: 'Jobs', verb: 'Querying job data' },

  // Part & Inventory tools
  { pattern: /part|inventory|where_used|transaction/, module: 'Parts', verb: 'Querying part data' },

  // AR tools
  { pattern: /ar_|invoice(?!.*ap)|aging(?!.*ap)|cash_receipt|receipt/, module: 'AR', verb: 'Checking receivables' },

  // AP tools
  { pattern: /ap_|vendor|ap_invoice/, module: 'AP', verb: 'Querying AP data' },

  // Purchasing tools
  { pattern: /purchas|po_|po\b/, module: 'Purchasing', verb: 'Querying purchasing data' },

  // Shipping tools
  { pattern: /ship(?!.*invoice)|pack/, module: 'Shipping', verb: 'Querying shipping data' },

  // Analytics tools
  { pattern: /analytics|cash_position|profitability|margin|digest/, module: 'Analytics', verb: 'Running analytics' },

  // Engineering tools
  { pattern: /engineer|bom|routing|method/, module: 'Engineering', verb: 'Querying engineering data' },

  // Quality tools
  { pattern: /quality|nonconform|ncr|inspection/, module: 'Quality', verb: 'Checking quality data' },

  // GL tools
  { pattern: /gl_|ledger|journal/, module: 'General Ledger', verb: 'Querying GL data' },

  // Labor tools
  { pattern: /labor/, module: 'Labor', verb: 'Querying labor data' },

  // Document tools
  { pattern: /document|attachment|pdf/, module: 'Documents', verb: 'Processing documents' },

  // Email/Calendar tools
  { pattern: /email|calendar|meeting|outlook/, module: 'Email', verb: 'Checking communications' },

  // Import Costing
  { pattern: /import|container|landed_cost/, module: 'Import Costing', verb: 'Calculating import costs' },

  // Config tools
  { pattern: /config|setting|setup/, module: 'Config', verb: 'Loading configuration' },
]

/**
 * Get user-facing module name for a tool.
 * @param {string} toolName - Internal tool name (e.g., "epicor_get_vendor")
 * @returns {string} Module label (e.g., "AP")
 */
export function getModuleLabel(toolName) {
  const normalized = toolName.toLowerCase().replace(/^epicor_/, '')
  for (const { pattern, module } of MODULE_MAP) {
    if (pattern.test(normalized)) return module
  }
  // Neutral fallback for any unmapped tool — never the status-sounding
  // "Processing" (which reads like a stuck chip).
  return 'Agent'
}

/**
 * Get user-facing verb for a tool (used during progress).
 * @param {string} toolName - Internal tool name
 * @returns {string} Action verb (e.g., "Querying AP data...")
 */
export function getModuleVerb(toolName) {
  const normalized = toolName.toLowerCase().replace(/^epicor_/, '')
  for (const { pattern, verb } of MODULE_MAP) {
    if (pattern.test(normalized)) return verb
  }
  return 'Working'
}

/**
 * Get module color class for a tool badge.
 * @param {string} toolName - Internal tool name
 * @returns {{ bg: string, text: string, border: string }}
 */
export function getModuleColor(toolName) {
  const mod = getModuleLabel(toolName)
  return MODULE_COLORS[mod] || MODULE_COLORS.default
}

const MODULE_COLORS = {
  Customers:      { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-200 dark:border-violet-800' },
  Orders:         { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' },
  Quotes:         { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800' },
  Jobs:           { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-800' },
  Parts:          { bg: 'bg-cyan-50 dark:bg-cyan-950/30', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-200 dark:border-cyan-800' },
  AR:             { bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800' },
  AP:             { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-300', border: 'border-red-200 dark:border-red-800' },
  Purchasing:     { bg: 'bg-indigo-50 dark:bg-indigo-950/30', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-200 dark:border-indigo-800' },
  Shipping:       { bg: 'bg-teal-50 dark:bg-teal-950/30', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-200 dark:border-teal-800' },
  Analytics:      { bg: 'bg-fuchsia-50 dark:bg-fuchsia-950/30', text: 'text-fuchsia-700 dark:text-fuchsia-300', border: 'border-fuchsia-200 dark:border-fuchsia-800' },
  Engineering:    { bg: 'bg-sky-50 dark:bg-sky-950/30', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-200 dark:border-sky-800' },
  Quality:        { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' },
  'General Ledger': { bg: 'bg-slate-50 dark:bg-slate-950/30', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-800' },
  Labor:          { bg: 'bg-purple-50 dark:bg-purple-950/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' },
  Documents:      { bg: 'bg-pink-50 dark:bg-pink-950/30', text: 'text-pink-700 dark:text-pink-300', border: 'border-pink-200 dark:border-pink-800' },
  Email:          { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' },
  'Import Costing': { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-800' },
  Config:         { bg: 'bg-gray-50 dark:bg-gray-950/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-800' },
  'Web Search':   { bg: 'bg-indigo-50 dark:bg-indigo-950/30', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-200 dark:border-indigo-800' },
  Knowledge:      { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-200 dark:border-violet-800' },
  Search:         { bg: 'bg-sky-50 dark:bg-sky-950/30', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-200 dark:border-sky-800' },
  Agent:          { bg: 'bg-gray-50 dark:bg-gray-950/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-800' },
  Processing:     { bg: 'bg-gray-50 dark:bg-gray-950/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-800' },
  default:        { bg: 'bg-gray-50 dark:bg-gray-950/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-800' },
}
